import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@r/api/auth.tsx';
import { type LinkProps, useLocation } from '@tanstack/react-router';
import type { UserWorkspaceMeReadModel } from '@r/api/generated/client.schemas.ts';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { isWaitingListEnabled as getIsWaitingListEnabled } from '@admin/pages/settings/purchase-proof-settings-utils.ts';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail
} from '@ui/sidebar.tsx';
import { WorkspaceSwitcher } from '@admin/components/workspace/workspace-switcher.tsx';
import { NavUser } from '@r/components/nav-user.tsx';
import { NavMain } from '@admin/components/navigation/nav-main.tsx';
import { WorkspaceClientLink } from '@admin/components/navigation/workspace-client-link.tsx';

import {
  ArchiveIcon,
  ChartLineIcon,
  CoinsIcon,
  FactoryIcon,
  GalleryVerticalEnd,
  Gamepad2Icon,
  LayoutDashboardIcon,
  StoreIcon,
  type LucideIcon,
  PaintbrushIcon,
  PenIcon,
  SettingsIcon,
  UsersIcon
} from 'lucide-react';

import { useRoutes } from '@r/hooks/use-routes.ts';
import { linksAvailable } from '@r/utils/utils.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { isTargetLogicWorkspace } from '@r/utils/workspace-utils.ts';
import { isStepLogicWorkspace } from '@r/utils/workspace-utils.ts';

interface MenuItemBase {
  title: string;
  icon?: LucideIcon;
  isActive?: boolean;
  isVisible?: boolean;
}

interface LeafMenuItem extends MenuItemBase {
  url: LinkProps['to'];
  items?: never;
}

interface ParentMenuItem extends MenuItemBase {
  url: LinkProps['to'] | '#';
  items: LeafMenuItem[];
}

type MenuItem = ParentMenuItem | LeafMenuItem;
type UserWorkspace = UserWorkspaceMeReadModel;

function isCurrentRouteWithLocation(
  location: ReturnType<typeof useLocation>,
  route: string | string[]
) {
  const paths = Array.isArray(route) ? route : [route];

  return paths.some((path) => {
    if (path.endsWith('/*') || path.endsWith('/:id') || path.endsWith('/$id')) {
      const base = path.replace(/\/(\*|:id)$/, '');

      if (path.endsWith('/*')) {
        return location.pathname === base || location.pathname.startsWith(`${base}/`);
      }

      return new RegExp(`^${base}/\\d+$`).test(location.pathname);
    }

    return location.pathname === path;
  });
}

const getData = (
  t: ReturnType<typeof useTranslation>['t'],
  routesData: ReturnType<typeof useRoutes>['data'],
  location: ReturnType<typeof useLocation>,
  userData: ReturnType<typeof useUser>['data'],
  isTargetLogic: boolean,
  isStepLogic: boolean,
  isWaitingListEnabled: boolean
): { navMain: MenuItem[] } => {
  const isVisibleRoutes = (routes: string[]) => {
    const links = linksAvailable(routesData, routes);
    return links.length > 0;
  };

  return {
    navMain: [
      {
        title: t('menu.admin.dashboard'),
        url: '/',
        icon: LayoutDashboardIcon,
        isActive: isCurrentRouteWithLocation(location, '/'),
        isVisible: isVisibleRoutes(['/']),
        items: []
      },
      {
        title: t('menu.admin.companies'),
        url: '#',
        icon: FactoryIcon,
        isActive: isCurrentRouteWithLocation(location, ['/companies/*', '/targets/import']),
        isVisible: isVisibleRoutes([
          '/companies',
          ...(isWaitingListEnabled ? ['/companies/waiting-list'] : []),
          '/companies/insights',
          '/companies/modify',
          '/companies/import',
          '/targets/import'
        ]),
        items: (
          [
            {
              title: t('menu.admin.companies.sub.list'),
              url: '/companies',
              isActive: isCurrentRouteWithLocation(location, ['/companies', '/companies/:id'])
            },
            {
              title: t('menu.admin.companies.sub.waiting.list'),
              url: '/companies/waiting-list',
              isActive: isCurrentRouteWithLocation(location, ['/companies/waiting-list']),
              isVisible: isWaitingListEnabled
            },
            {
              title: t('menu.admin.companies.insights'),
              url: '/companies/insights',
              isActive: isCurrentRouteWithLocation(location, '/companies/insights')
            },
            {
              title: t('menu.admin.companies.sub.import'),
              url: '/companies/import',
              isActive: isCurrentRouteWithLocation(location, '/companies/import')
            },
            {
              title: t('menu.admin.companies.sub.modify'),
              url: '/companies/modify',
              isActive: isCurrentRouteWithLocation(location, '/companies/modify')
            },
            {
              title: t('menu.admin.companies.sub.targets.import'),
              url: '/targets/import',
              isActive: isCurrentRouteWithLocation(location, '/targets/import')
            }
          ] satisfies LeafMenuItem[]
        ).filter((item) => item.isVisible !== false)
      },
      {
        title: t('menu.admin.contacts'),
        url: '#',
        icon: UsersIcon,
        isActive: isCurrentRouteWithLocation(location, ['/users/*']),
        isVisible: (() => {
          const visible = isVisibleRoutes(['/users', '/users/insights', '/users/import']) || false;
          if (visible) return true;
          const activeWorkspace = getActiveWorkspaceUniversal(userData?.visibleWorkspaces ?? []);
          const roles: string[] = (activeWorkspace?.roles as string[]) ?? [];
          return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_FIDDY_ADMIN');
        })(),
        items: [
          {
            title: t('menu.admin.contacts.sub.list'),
            url: '/users',
            isActive: isCurrentRouteWithLocation(location, ['/users', '/users/:id'])
          },
          {
            title: t('menu.admin.contacts.insights'),
            url: '/users/insights',
            isActive: isCurrentRouteWithLocation(location, '/users/insights')
          },
          {
            title: t('menu.admin.contacts.sub.import'),
            url: '/users/import',
            isActive: isCurrentRouteWithLocation(location, '/users/import')
          }
        ]
      },
      {
        title: t('menu.admin.points'),
        url: '#',
        icon: CoinsIcon,
        isActive: isCurrentRouteWithLocation(location, ['/points/*']),
        isVisible: isVisibleRoutes(['/points', '/points/insights', '/points/import']),
        items: [
          {
            title: t('menu.admin.points.my'),
            url: '/points',
            isActive: isCurrentRouteWithLocation(location, '/points')
          },
          {
            title: t('menu.admin.points.insights'),
            url: '/points/insights',
            isActive: isCurrentRouteWithLocation(location, '/points/insights')
          },
          {
            title: t('common.add.point'),
            url: '/points/import',
            isActive: isCurrentRouteWithLocation(location, '/points/import')
          }
        ]
      },
      {
        title: t('menu.admin.performances'),
        url: '#',
        icon: ChartLineIcon,
        isActive: isCurrentRouteWithLocation(location, ['/performances/*']),
        isVisible: isVisibleRoutes([
          '/performances',
          '/performances/import',
          '/performances/insights',
          '/performances/products-references',
          '/performances/purchase-proofs',
          '/performances/targets',
          '/performances/steps'
        ]),
        items: [
          {
            title: t('menu.admin.performances.my'),
            url: '/performances',
            isActive: isCurrentRouteWithLocation(location, '/performances')
          },
          ...(isTargetLogic
            ? [
                {
                  title: t('menu.admin.performances.targets'),
                  url: '/performances/targets' as const,
                  isActive: isCurrentRouteWithLocation(location, '/performances/targets')
                }
              ]
            : []),
          ...(isStepLogic
            ? [
                {
                  title: t('menu.admin.performances.steps'),
                  url: '/performances/steps' as const,
                  isActive: isCurrentRouteWithLocation(location, '/performances/steps')
                }
              ]
            : []),
          {
            title: t('menu.admin.performances.insights'),
            url: '/performances/insights',
            isActive: isCurrentRouteWithLocation(location, '/performances/insights')
          },
          {
            title: t('common.add.performance'),
            url: '/performances/import',
            isActive: isCurrentRouteWithLocation(location, '/performances/import')
          },
          {
            title: t('menu.admin.products.references'),
            url: '/performances/products-references',
            isActive: isCurrentRouteWithLocation(location, '/performances/products-references')
          },
          {
            title: t('menu.admin.performances.purchase.proofs'),
            url: '/performances/purchase-proofs',
            isActive: isCurrentRouteWithLocation(location, '/performances/purchase-proofs')
          }
        ]
      },
      {
        title: t('menu.admin.shops'),
        url: '#',
        icon: StoreIcon,
        isActive: isCurrentRouteWithLocation(location, ['/shops/*']),
        isVisible: isVisibleRoutes(['/shops', '/shops/orders']),
        items: [
          {
            title: t('menu.admin.order.tracking'),
            url: '/shops/orders',
            isActive: isCurrentRouteWithLocation(location, [
              '/shops/orders',
              '/shops/orders/:id',
              '/shops/orders/:id/reward-obtained/:productId'
            ])
          },
          {
            title: t('menu.admin.shops.insights'),
            url: '/shops/orders/insights',
            isActive: isCurrentRouteWithLocation(location, '/shops/orders/insights')
          },
          {
            title: t('menu.admin.shops.list'),
            url: '/shops',
            isActive: isCurrentRouteWithLocation(location, '/shops')
          }
        ]
      },
      {
        title: t('menu.admin.animations'),
        url: '#',
        icon: Gamepad2Icon,
        isActive: isCurrentRouteWithLocation(location, ['/games/*', '/boosters/*']),
        isVisible: isVisibleRoutes(['/games', '/boosters']),
        items: [
          {
            title: t('menu.admin.animations.management'),
            url: '/games',
            isActive: isCurrentRouteWithLocation(location, '/games')
          },
          {
            title: t('menu.admin.animations.boosters'),
            url: '/boosters',
            isActive: isCurrentRouteWithLocation(location, '/boosters')
          }
        ]
      },
      {
        title: t('menu.admin.content'),
        url: '#',
        icon: PenIcon,
        isActive: isCurrentRouteWithLocation(location, ['/content/*']),
        isVisible: isVisibleRoutes(['/content/content-block']),
        items: [
          {
            title: t('menu.admin.personnalisation.bloc.contenu'),
            url: '/content/content-block',
            isActive: isCurrentRouteWithLocation(location, '/content/content-block/*')
          }
        ]
      },
      {
        title: t('menu.admin.resources'),
        url: '#',
        icon: ArchiveIcon,
        isActive: isCurrentRouteWithLocation(location, ['/ressources/*']),
        isVisible: isVisibleRoutes(['/ressources/video-instructions']),
        items: [
          {
            title: t('menu.admin.resources.video.notices'),
            url: '/ressources/video-instructions',
            isActive: isCurrentRouteWithLocation(location, '/ressources/video-instructions')
          }
        ]
      },
      {
        title: t('menu.admin.personnalisation'),
        url: '/personalization',
        icon: PaintbrushIcon,
        isActive: isCurrentRouteWithLocation(location, ['/personalization', '/personalization/*']),
        isVisible: isVisibleRoutes([
          '/personalization',
          '/personalization/translations',
          '/personalization/assets'
        ]),
        items: []
      },
      {
        title: t('menu.admin.configuration'),
        url: '/settings',
        icon: SettingsIcon,
        isActive: isCurrentRouteWithLocation(location, ['/settings', '/settings/*']),
        isVisible: isVisibleRoutes([
          '/settings',
          '/settings/api/tokens',
          '/settings/typologies',
          '/settings/scheduled-tasks'
        ]),
        items: []
      }
    ]
  };
};

function getWorkspaceSelectOptions(userWorkspaces: UserWorkspace[]) {
  return userWorkspaces.map((workspace) => ({
    active: workspace.active,
    name: workspace.workspace.name,
    logo: GalleryVerticalEnd,
    plan: 'Workspace',
    value: workspace.workspace.id,
    roles: workspace.roles.filter((role): role is string => role != null)
  }));
}

export function AdminSidebar() {
  const user = useUser();
  const { t } = useTranslation();
  const routes = useRoutes();
  const location = useLocation();
  const userWorkspaces: UserWorkspace[] = user.data?.visibleWorkspaces ?? [];
  const activeWorkspace = getActiveWorkspaceUniversal(userWorkspaces);
  const { workspace } = UseWorkspace(user.data);
  const isTargetLogic = isTargetLogicWorkspace(workspace);
  const isStepLogic = isStepLogicWorkspace(workspace);
  const workspaceConfig = useContext(WorkspaceConfigContext);
  const isWaitingListEnabled = getIsWaitingListEnabled(
    workspaceConfig?.groupedConfigs?.core?.['accept-waiting-list'] as string | undefined
  );

  function getUserFullName() {
    return `${user.data?.firstname ?? ''} ${user.data?.lastname ?? ''}`.trim();
  }

  return (
    <Sidebar collapsible="icon" className="bg-sidebar">
      {activeWorkspace && (
        <SidebarHeader>
          <WorkspaceSwitcher workspaces={getWorkspaceSelectOptions(userWorkspaces)} />
        </SidebarHeader>
      )}

      <SidebarContent>
        <NavMain
          items={
            getData(
              t,
              routes.data,
              location,
              user.data,
              isTargetLogic,
              isStepLogic,
              isWaitingListEnabled
            ).navMain
          }
        />
      </SidebarContent>

      <SidebarFooter>
        <WorkspaceClientLink domain={workspace?.domain} />
        <NavUser
          user={{
            name: getUserFullName(),
            email: user.data?.email ?? '',
            avatar: user.data?.avatar ?? null
          }}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
